// ./fileManagerを汚したくないのでこちらをいじることとする

import { newDefaultData } from '@/appLogic/importData/initialData';

import {
  TranslateExcelBusiness,
  AddPastAchievementInfoDataByType,
  AddPastAchievementInfoType,
  isValidTranslateBusiness,
  DifferenceConfirmationCodeAndName,
  ExcelYearMonth,
  SupplierForecastAggregateInfo,
  InputExcelType,
} from '@/coreDomainsModule/translateExcel';
import { ISalesResult } from '@/coreDomainsModule/entities/salesResults';
import { IAnnounce } from '@/coreDomainsModule/entities/forecast';
import { IArrival } from '@/coreDomainsModule/entities/arrival';
import { IMvpInventory } from '@/coreDomainsModule/entities/inventory';
import { IYearMonth } from '@/coreDomainsModule/date';
import XLSX from 'xlsx';
import _ from 'lodash';
import { i18n } from '@/i18n';
import { saveAs } from 'file-saver';
import { simpleCopy } from '@/utils/deepCopy';
import {
  ExcelTranslateError,
  ExcelParseOption,
  IOutputFileInfo,
  OutputFileType,
  ProgressUpdateFunction,
  IOutputExcelInfoTest,
  ExcelOutputRow,
  ExcelOutputResult,
  OutputFileContents,
  OutputFileContent,
} from '../../../coreDomainsModule/translateExcel';
import { addFileNameToOutputExcelOption, defaultOutputExcelPageNames } from '@/coreDomainsModule/excelParseOptions';
import { filterStringNumberUndefinedToString } from '@/coreDomainsModule/extends/string';
import { compareTranscriptionLogOrder } from '@/appLogic/translateCompare';
import { getLeadTimeForSpanText } from '@/appLogic/business/inventorySimulation';
import { getTranslateBusinessIdManager } from '@/utils/translateBusinessIdManager';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { ExportFileType } from '@/coreDomainsModule/exportSupplierForecastExcel';
import { fm } from '@/appLogic/fileManager/baseFileManager';
import { CreateCsvUseCase } from '@/exportFileUseCasesModule/createCsvUseCase';
import { ExportExcelParseOption } from '@/appLogic/interfaces/exportExcel';
import { IUnmanagedBusiness } from '@/coreDomainsModule/entities/unmanagedBusiness';
import {
  validateFailedInputExcel,
  validateOutputExcelDifferenceConfirmationCodeAndName,
  validateOutputFoundationParameters,
} from './validateTranslateExcel';
import { TranslateMode } from '@/registerInputModule/pages/interfaces/translateExcelStore';
import {
  bringCellValue,
  fetchRowIndex,
  convertBufferToWorkSheet,
  outputAmountValue,
  escapeStringComma,
  checkFalsyNumber,
} from './excelUtils';
import { registerErrorInformation } from './excelValidates';
import { TranslateLogger } from './translateLogger';
import { ExportSupplierForecastExcelService } from '@/coreDomainsModule/services/exportSupplierForecastExcelService';

const date = new Date();
const t = i18n.t;

/**
 * エクセル転記（出力）に関するロジックをまとめる
 */
export class TranslateOutputFileManager {
  private translateLogger: TranslateLogger;
  private inputFileTypes: InputExcelType[];

  /**
   * コンストラクタ
   * @param latestYearMonthData 最新の年月データ
   * @param inputFileTypes 入力エクセルの種類
   */
  constructor(latestYearMonthData: ExcelYearMonth, inputFileTypes: InputExcelType[]) {
    this.translateLogger = new TranslateLogger(latestYearMonthData);
    this.inputFileTypes = inputFileTypes;
  }

  public eventParseFile = (e: Event) => {
    const files = (e.target as HTMLInputElement).files;
    if (!files) return;
    return files[0] as File;
  };

  // 在庫表エクセルを受け取り、「在庫」「内示」「販売実績」「入荷」エクセルの情報を転記する
  public createOrderExcel = async (
    file: File,
    formArray: Record<string, TranslateExcelBusiness>,
    inputErrorArray: ExcelTranslateError[],
    outputFileType: OutputFileType,
    updateProgressFunc: ProgressUpdateFunction,
    executionDate: Date,
    successTranslateKeys: Record<string, boolean>,
    translateMode: TranslateMode
  ): Promise<OutputFileContent> => {
    const outputFileContents: OutputFileContent = [[], new Blob(), [], [], [], []];
    const outputExcelInfoTest: IOutputExcelInfoTest[] = [];
    const translateErrorObj: ExcelTranslateError[] = []; // 転記時に発生したエラーをまとめる(現状は未転記取引のみ)
    const translateUnmanagedBusiness: IUnmanagedBusiness[] = [];

    if (!file) return outputFileContents;
    const f = file;

    // 転記エクセルを読み取るためのオプション
    const outputExcelPageNames: [[string, string], ExcelParseOption][] = addFileNameToOutputExcelOption(
      file.name,
      defaultOutputExcelPageNames
    );
    // const outputExcelPageNames: string[] = ['ﾃﾞﾙﾍﾟｯﾄ'];

    // xlsmの方で一旦解析をしておいてそれを用いてXlsxPopulateで貼り付ける方針で
    const data = await fm.convertFileToBuffer(f);
    const rowExcelData = XLSX.read(data, { type: 'array' });
    const translateLog: string[][] = [];
    // 変更履歴の列名を決定
    // TODO: 列名系別ファイルにまとめたい
    translateLog.push([
      'シート名',
      'セル番号',
      '置き場名',
      '商品CD',
      '商品名',
      '容量',
      '仕入先名',
      '売上先名',
      '送り先名',
      '列名',
      '転記前',
      '転記後',
      '差分',
    ]);
    const workbook: any = await convertBufferToWorkSheet(data);
    const translateErrorLog: string[][] = [];
    await updateProgressFunc(`${t(outputFileType)} ${t('読み込み中・・・')}`, 0);

    // まずXlsxPopulate出力用のデータを作成する
    for (const pageInfo of outputExcelPageNames) {
      // 転記先のシートが存在しない場合、読み飛ばす
      if (!rowExcelData.Sheets[pageInfo[0][0]]) continue;
      const sheetName: string = pageInfo[0][0];
      const sheet = rowExcelData.Sheets[sheetName];
      const outputExcelRowInfo: ExcelOutputRow[] = TranslateOutputFileManager.parseOutputExcelObjects(
        sheet,
        pageInfo[0][1],
        outputFileType,
        pageInfo[1]
      );
      // ここがおかしい（メモ）　formArrayとoutputExcelRowInfoは無事

      const outputExcelAddressInfo: ExcelOutputResult = this.splitOutputExcelData(
        outputExcelRowInfo,
        formArray,
        inputErrorArray,
        successTranslateKeys,
        outputFileType,
        executionDate,
        file.name,
        sheetName
      );

      if (process.env.NODE_ENV === 'test') {
        // テスト用
        outputExcelInfoTest.push({
          label: sheetName,
          data: outputExcelAddressInfo,
        });
      }

      // 転写
      let worksheet = workbook.sheet(sheetName);
      // 転記先の仕入先内示エクセルで、列名の年月部分、過去1ヶ月分の内示数量の更新、各取引数量の列に対して更新処理(転記するための準備)を行う
      if (outputFileType === '仕入先内示') {
        worksheet = this.updateSupplierAnnounceWorkSheet(worksheet, pageInfo[1], sheet['!ref']);
      }
      for (const k of Object.keys(outputExcelAddressInfo)) {
        if (k !== 'undefined') {
          const cellAdress = XLSX.utils.decode_cell(k);
          const startAddress = XLSX.utils.decode_cell(pageInfo[1].separateYmd.startCell);
          // 仕入先内示への転記時、過去1ヶ月分の内示数量が入っている列へ転記しないようにする
          if (cellAdress.c === startAddress.c && outputFileType === '仕入先内示') continue;
          // 参考：https://github.com/dtjohnson/xlsx-populate#style-reference
          const translateValue: string = outputAmountValue(outputExcelAddressInfo[k]?.value);
          if (translateValue !== '') {
            // 転記可能（何かしら存在している）
          } else {
            // 転記不可能（転記元に何も存在していない）
            // TODO: この分岐で本来転記失敗の処理が必要
          }
          if (outputExcelAddressInfo[k].isDuplicatedBusiness) {
            worksheet.cell(k).value(escapeStringComma(translateValue));
          } else {
            worksheet.cell(k).value(translateValue.parseNumber());
          }
          // 文字色を赤に
          worksheet.cell(k).style('fontColor', 'ff0000');

          // 変更履歴の作成を行う
          // 変更履歴情報を追加
          translateLog.push(this.translateLogger.createTranslateLog(outputExcelAddressInfo, sheetName, k));
        }
      }
      // 仕入先内示の集計行に数量の項目別集計値を入力する
      if (outputFileType === '仕入先内示') {
        worksheet = this.updateTotalAnnounceWorkSheet(worksheet, pageInfo[1], sheet['!ref']);
      }
      // 保持しているTranslateBusinessの内示、販売実績情報に在庫表エクセルから読み取った情報を追加する
    }
    await updateProgressFunc(t(`${t(outputFileType)} ${t('転記中・・・')}`), 0);

    // 入力ファイルの読み込みができているかバリデーションを行う
    validateFailedInputExcel(formArray, successTranslateKeys, this.inputFileTypes, inputErrorArray);

    // 転写に失敗したものをエラーとして計上する
    Object.keys(formArray).forEach((input) => {
      if (!successTranslateKeys[input]) {
        // 転写がされていないinputFormArray
        const failedTranslateObject: TranslateExcelBusiness = simpleCopy(formArray[input]);
        for (const info of failedTranslateObject.excelInfos ?? []) {
          if (!info.rowObjectInfo.translateBusinessId && !info.rowObjectInfo.businessNumber) continue; // 何も情報が入っていない行を出力しないように
          registerErrorInformation(
            translateErrorObj,
            info,
            info.rowObjectInfo,
            input,
            '転記先が存在しない',
            undefined,
            translateUnmanagedBusiness
          );
        }
      }
    });

    // NOTE: 未転記取引情報のみを抽出（初回転記の時だけ、有効な行のみ出してあげる）
    // 転記時エラーの項目が増えた時のために残しておく
    const filteredErrorObj = translateErrorObj
      .filter((item) => {
        switch (translateMode) {
          case 'translateNewExcel':
            return item.errCode === '転記先が存在しない' && item.value && isValidTranslateBusiness(item.value);
          case 'translateUpdateExcel':
          case 'updateExcel':
          default:
            return item.errCode === '転記先が存在しない';
        }
      })
      .map((item) => ({ ...item }));

    // 重複取引（入力ファイルの）に関するエラーを処理する
    const filteredInputErrorLog: string[][] = [];
    const filteredInputErrorObj = inputErrorArray.filter((item) => item.errCode === '取引情報が重複している').map((item) => ({ ...item }));
    if (filteredInputErrorObj.length > 0) {
      this.translateLogger.createErrorLogData(filteredInputErrorLog, filteredInputErrorObj, '取引情報が重複している');
    }

    // 未転記取引まとめ.csvにエラーとして計上したものを出力する
    this.translateLogger.createErrorLogData(translateErrorLog, filteredErrorObj, '転記先が存在しない', outputFileType);

    // 一旦仮置き
    // TODO: 別ファイルにおきたい
    const translateColInfo: string[] = [
      'シート名',
      'セル番号',
      '置き場名',
      '商品CD',
      '商品名',
      '容量',
      '仕入先名',
      '送り先名',
      '売上先名',
      '列名',
    ];

    const translateErrorColInfo: string[] = [
      'エラー名',
      '対象ファイル',
      'シート名',
      '対象セル',
      '置き場',
      '商品CD',
      '商品名',
      '容量',
      '仕入先名',
      '送り先名',
      '売上先名',
    ];

    // TODO: ここ定数で管理した方がいい気がする。。。
    // 一旦時間ないので放置、時間ある時誰か直して欲しいです
    // 比較処理を行う際、文字列と数字の複合型みたいなものは文字コードの関係か比較が期待通りにならない
    // ↑のため、「セル番号」列のindexを取得し、比較関数内でどのindexが「セル番号」列かを見て処理を分けるようにしている
    // 列名が「セル番号」となっている場所を取得する
    const translateLogCellAddressColIndex: number[] = [translateLog[0].indexOf('セル番号')];
    translateLogCellAddressColIndex.push(translateLog[0].indexOf('列名'));
    // 列名が「セル番号」となっている場所を取得する
    const translateErrorLogCellAddressColIndex: number[] = [translateErrorLog[0].indexOf('対象セル')];

    // await Promise.all([
    // ]);
    // 項目(10個)の左から昇順に変更履歴を並び替える
    translateLog.sort(compareTranscriptionLogOrder(10, translateColInfo, translateLogCellAddressColIndex));
    // 項目（11個）の左から昇順に未転記取引まとめを並び替える
    translateErrorLog.sort(compareTranscriptionLogOrder(11, translateErrorColInfo, translateErrorLogCellAddressColIndex));

    // ファイル出力
    const wbout = await workbook.outputAsync();
    const blob = new Blob([wbout], { type: 'application/octet-stream' });

    // const start = performance.now();
    // const end = performance.now();
    // console.log(`Execution time: ${end - start} ms`);

    // ここから
    // Resolve issue https://github.com/lean-path1201/leanpath/issues/524
    // createCsv(translateErrorLog, 'エラーまとめ');

    // ここまで　隔離する
    return [outputExcelInfoTest, blob, translateLog, translateErrorLog, filteredInputErrorLog, translateUnmanagedBusiness];
  };

  /**
   * createOrderExcel：ファイル出力用
   * @param outputObject 出力するエクセル情報を入れるオブジェクト
   * @param outputFileInfo createOutputExcel()で作成した情報
   * @param outputFileType 出力する転記エクセル(在庫表または仕入先内示)
   */
  public downloadResultOfCreateOrderExcel = (
    outputObject: OutputFileContents,
    outputFileInfo: IOutputFileInfo,
    outputFileType: OutputFileType
  ) => {
    const createCsvUseCase = new CreateCsvUseCase();

    const nowDate = new Date();
    // 対象転記エクセルが出力選択ボタンで未選択になっていた場合、処理を行わない
    // 取引重複エラー追加により、こいつがactiveの場合は処理を続行することにする（これ以外は全部falseの前提)
    if (!outputFileInfo[outputFileType].active && !outputFileInfo['取引重複エラー'].active) return;
    const createdDate: string =
      String(nowDate.getFullYear()) + DateService.convertMonthWithZero(nowDate.getMonth() + 1) + String(nowDate.getDate());
    // 出力するファイル、しないファイルの情報をもとに結果を出力する
    Object.keys(outputFileInfo).forEach((key) => {
      // 作成対象エクセルでない場合は処理を飛ばす
      if (outputFileInfo[key].active) {
        switch (outputFileInfo[key].label) {
          case '在庫表':
            if (outputFileInfo[key].label !== outputFileType) return;
            saveAs(outputObject[outputFileType][1], t('転記出力_エクセル', { outputFileType, createdDate }) + '.xlsx');
            break;
          case '変更履歴':
            createCsvUseCase.Execute(outputObject[outputFileType][2], t('転記出力_変更履歴', { outputFileType, createdDate }));
            break;
          case '未転記取引まとめ':
            createCsvUseCase.Execute(outputObject[outputFileType][3], t('転記出力_未転記取引まとめ', { outputFileType, createdDate }));
            break;
          case '取引重複エラー':
            createCsvUseCase.Execute(outputObject[outputFileType][4], t('転記出力_取引重複エラー', { createdDate }));
            break;
          case '仕入先内示':
            if (outputFileInfo[key].label !== outputFileType) return;
            saveAs(outputObject[outputFileType][1], t('転記出力_エクセル', { outputFileType, createdDate }) + '.xlsx');
            break;
          default:
            console.error('不正な出力ファイル形式');
            break;
        }
      }
    });
  };

  /**
   * 在庫表（出力ファイル）のoutputを作成するためのExcelOutputRow[]を作成する関数
   * @param workSheet ワークシート情報
   * @param startCell 表が始まっているセルの番号
   * @param fileType 解析するエクセルの種類
   * @param option parseExcelObjectsで色々やるためのオプション
   */
  /* TODO:リファクタ対象 */
  public static parseOutputExcelObjects = (
    workSheet: XLSX.WorkSheet,
    startCell: string,
    fileType: OutputFileType | ExportFileType,
    option?: ExcelParseOption | ExportExcelParseOption
  ): ExcelOutputRow[] => {
    //セルの範囲
    const output: ExcelOutputRow[] = [];
    const headerStrings: string[] = []; // 重複を許可したいため配列にしてます
    const startCellVal = XLSX.utils.decode_cell(startCell);
    const separateYmdData: string[] = [];
    const range = workSheet['!ref'];
    let findHeaderRow = -1;
    const ignoreRows: Array<number> = [];
    // convert index
    for (const row of option ? option.ignoreRow : []) {
      ignoreRows.push(XLSX.utils.decode_cell(row).r);
    }
    if (!range) return [];
    const rangeVal = XLSX.utils.decode_range(range);

    // 0-indexとn-indexの差を埋める（表のはじまっているシートが基本0,0の位置のため0-index)
    const baseColumn = startCellVal.c;

    // console.log('一旦', { range: range, startCellVal: startCellVal });
    // 実績部分がN個ある形式に対応
    if (option) {
      const startAddress = XLSX.utils.decode_cell(option.separateYmd.startCell);
      for (let c = startAddress.c; c <= rangeVal.e.c; c = c + option.separateYmd.separateCount) {
        const adr = XLSX.utils.encode_cell({ c: c, r: startAddress.r });
        const cell = workSheet[adr];
        const cellValue = bringCellValue(cell);
        separateYmdData.push(cellValue);
      }
    }
    // workSheet['!ref']がpaddingのような切り取り方をしているのでとりあえずmax-1行目までの抽出としています
    for (let r = 0; r <= rangeVal.e.r; r++) {
      if (ignoreRows.includes(r)) continue;
      const rowInfo: ExcelOutputRow = Object.assign({}, {});
      let updatedRow = false;
      for (let c = 0; c <= rangeVal.e.c; c++) {
        const adr = XLSX.utils.encode_cell({ c: c, r: r });
        const cell = workSheet[adr];
        const cellValue = bringCellValue(cell);
        // 対象範囲
        if (startCellVal.c <= c && startCellVal.r <= r) {
          if (findHeaderRow == -1 || findHeaderRow == r) {
            // 出力ファイルによってヘッダーの追加処理を分岐
            headerStrings.push(cellValue); // ヘッダーの追加
            findHeaderRow = r; // 最初の１行目だけ見たい（Header)
            // console.log(`${adr} type:${cell.t} value:${cell.v} text:${cell.w}`);
            continue;
          }
          // 空として登録されてるヘッダーについては無視する(セルが結合等されていてindexずれしていたのがうざかったのでこうしています)
          if (headerStrings[c - baseColumn] !== '' && headerStrings[c - baseColumn]) {
            // columnIndexは1-indexとして扱いたい
            let rawKey = fetchRowIndex(headerStrings[c - baseColumn], c - baseColumn, separateYmdData, option);
            rawKey = this.updateRowKeyIfNeeded(fileType, rawKey, headerStrings[c - baseColumn]);
            if (!rowInfo?.[rawKey]) {
              // 既に同じキーが存在している場合は無視する
              rowInfo[rawKey] = {
                value: cellValue,
                address: adr,
                prevAddress: undefined,
              };
            }
            updatedRow = true; // 1つでもデータが入っていたら更新フラグを立てる
          }
        }
        // console.log(`${adr} type:${cell.t} value:${cell.v} text:${cell.w}`);
      }
      if (updatedRow) {
        output.push(rowInfo);
      }
    }
    return output;
  };
  /**
   * 在庫表（出力ファイル）のoutputに転写するためのデータを作成する関数
   * @param excelData エクセルデータ(output)一行をkey-valueに変換したオブジェクト
   * @param inputFormArray IBusinessをラップしたエクセル転記情報の配列
   * @param inputErrorObj 入力エラー情報のオブジェクト
   * @param successTranslateKeys 転記が成功したエクセルデータのキー
   * @param fileType 出力するファイルの種類
   * @param executionDate 処理実行日
   * @param fileName 処理実行ファイル名
   * @param sheetName 処理実行シート名
   */
  public splitOutputExcelData = (
    excelData: ExcelOutputRow[],
    inputFormArray: Record<string, TranslateExcelBusiness>,
    inputErrorObj: ExcelTranslateError[],
    successTranslateKeys: Record<string, boolean>,
    fileType: OutputFileType,
    executionDate: Date,
    fileName: string,
    sheetName: string
  ): ExcelOutputResult => {
    // 基本的な要素＋salesPositionに関してはここ、それ以外に関してはExtraMapにて処理を実行する
    const output: ExcelOutputResult = {};
    // CDと名称の差異確認用のオブジェクト
    const differenceConfirmationCodeAndName: DifferenceConfirmationCodeAndName = {};
    // 重複している取引IDの配列を作成
    const duplicatedTranslateKeys = Array.from(
      new Set(inputErrorObj.filter((value) => value.errCode === '取引情報が重複している').map((value) => value.key))
    );
    excelData.forEach((data, idx) => {
      // if (idx !== 0) formArray.push(JSON.parse(JSON.stringify(newDefaultData)));
      // try {
      // MVP ver1.0.0
      // 在庫表には置き場CD、商品CD、容量、仕入先CD、送り先CD、売上先CDが存在する
      // 内示は内示エクセルの置き場CD、商品CD、容量、仕入先CD、送り先CD、売上先CDと在庫表が合致する行に転記
      // 販売実績は販売実績エクセルの置き場CD、商品CD、容量、仕入先CD、送り先CD、売上先CDと在庫表が合致する行に転記
      // 入荷は入荷エクセルの置き場CD、商品CD、容量、仕入先CDと在庫表が合致する集計行に転記
      // 在庫は在庫エクセルの置き場CD、商品CD、容量、仕入先CDと在庫表が合致する集計行に転記
      // ~名(CDと1対１対応)は、在庫表の値を採用する（転記には用いない）

      const business: TranslateExcelBusiness = JSON.parse(JSON.stringify(newDefaultData));
      business.warehouse = filterStringNumberUndefinedToString(data['置場名']?.value);
      business.warehouseNumber = filterStringNumberUndefinedToString(data['置場CD']?.value);
      business.businessNumber = filterStringNumberUndefinedToString(data['商品CD']?.value);
      business.businessName = filterStringNumberUndefinedToString(data['商品名']?.value);
      business.package = filterStringNumberUndefinedToString(data['容量']?.value).parseNumberStringAndRemoveString(); // NOTE: 容量に関しては、文字が含まれていた場合「0.01」として扱う
      business.salesDestinationName = filterStringNumberUndefinedToString(data['送り先名']?.value);
      business.salesDestinationNumber = filterStringNumberUndefinedToString(data['送り先CD']?.value);
      business.customerName = filterStringNumberUndefinedToString(data['売上先名']?.value);
      business.customerNumber = filterStringNumberUndefinedToString(data['売上先CD']?.value);
      business.supplierName = filterStringNumberUndefinedToString(data['仕入先名']?.value);
      business.supplierNumber = filterStringNumberUndefinedToString(data['仕入先CD']?.value);

      // IDは上記の複合キーとする
      business.translateBusinessId = getTranslateBusinessIdManager().generateBusinessIdFromExcelInput(business, fileType);

      // 転記するもの
      // 1. 入荷
      // 2. 実績
      // 3. 内示
      // 4. (確)月末在庫(?)
      // 有効行のみ取り込む
      if (business.translateBusinessId) {
        // output[business.translateBusinessId] = business;
        // 本当はこんな感じでキーが存在するもの（＝転記するべきものが存在）しか更新しないようにするべきだが一旦コメントアウトしている
        if (inputFormArray[business.translateBusinessId]) {
          // 転写の対応が取れたものをメモしておく
          successTranslateKeys[business.translateBusinessId] = true;
          // 取引重複があるかどうかのフラグ
          const isDuplicatedBusiness = duplicatedTranslateKeys.includes(business.translateBusinessId);
          // 内示の転記 キー名：YM_内示
          const announceData: IAnnounce[] = inputFormArray[business.translateBusinessId]?.customerAnnounce ?? [];
          this.translateOutputValues(announceData, output, '内示', data, fileType, isDuplicatedBusiness);
          // 過去の実績数量の取得を行うため、在庫表エクセルの情報から内示情報の追加を行う
          this.addPastAchievementInfo(announceData, data, '内示');
          inputFormArray[business.translateBusinessId].customerAnnounce = announceData;
          // 入荷の転記 キー名：YM_入庫
          const arrivalData: IArrival[] = inputFormArray[business.translateBusinessId]?.arrivals ?? [];
          this.translateOutputValues(arrivalData, output, '入庫', data, fileType);
          // 販売履歴の転記 キー名：YM_実績
          const salesResultsData: ISalesResult[] = inputFormArray[business.translateBusinessId]?.salesResults ?? [];
          if (fileType === '在庫表') {
            this.translateOutputValues(salesResultsData, output, '実績', data, fileType);
            // 過去の実績数量の取得を行うため、在庫表エクセルの情報から販売実績情報の追加を行う
            this.addPastAchievementInfo(salesResultsData, data, '実績');
            inputFormArray[business.translateBusinessId].salesResults = salesResultsData;
          } else {
            // 今のところは、仕入先内示の場合
            this.translateOutputValues(salesResultsData, output, '販売実績', data, fileType);
          }
          // 在庫の転記 キー名：YM_(確)月末在庫
          const inventoryData: IMvpInventory[] = inputFormArray[business.translateBusinessId]?.mvpInventory ?? [];
          this.translateOutputValues(inventoryData, output, '(確)月末在庫', data, fileType);
          // TODO: 在庫に関しては転記対象とかが不確定なので一旦保留

          // 単位の読み取り(加工なし)
          inputFormArray[business.translateBusinessId].unit = filterStringNumberUndefinedToString(data['単位']?.value);

          // 任意のメモ項目
          if (data['顧客台帳ID']) {
            // 顧客台帳ID
            // TODO: businessMemoはユーザーに設定してもらう項目なので、設定機能ができたらそこで登録したものをキーとして登録できるようにする
            inputFormArray[business.translateBusinessId].businessMemo = {
              顧客台帳ID: filterStringNumberUndefinedToString(data['顧客台帳ID'].value),
            };
          }

          // 置場名、商品名、送り先名、売上先名、仕入先名を取り込む（在庫表の値を採用する。入力エクセルの値は無視）
          inputFormArray[business.translateBusinessId].warehouse = business.warehouse;
          inputFormArray[business.translateBusinessId].businessName = business.businessName;
          inputFormArray[business.translateBusinessId].salesDestinationName = business.salesDestinationName;
          inputFormArray[business.translateBusinessId].customerName = business.customerName;
          inputFormArray[business.translateBusinessId].supplierName = business.supplierName;

          if (filterStringNumberUndefinedToString(data['集計']?.value) !== '') {
            // ここを通ったIBusinessに関しては、Leanpathのシステムに登録する。
            // システムに反映したいプロパティがある場合、こちらで反映してください。

            // 取引として登録するフラグ
            inputFormArray[business.translateBusinessId].aggregateBusiness = true;

            // リードタイムを取り込む
            const leadTimeText = Number(data['リードタイム']?.value);
            const leadTimeSpanText = String(data['リードタイム単位']?.value);
            inputFormArray[business.translateBusinessId].leadTimeUnit = leadTimeSpanText?.toString(); // バリデーション用に単位を保持
            inputFormArray[business.translateBusinessId].leadTime = getLeadTimeForSpanText(leadTimeText, leadTimeSpanText);

            // 標準在庫指数を取り込む
            inputFormArray[business.translateBusinessId].stdInventoryIndices = {
              標準在庫指数: {
                min: Number(data['標準在庫指数(min)']?.value),
                max: Number(data['標準在庫指数(max)']?.value),
                option: {
                  unit: String(data['標準在庫指数単位']?.value),
                },
              },
            };

            // executionDateが属する月の在庫量をinputFormArray[business.translateBusinessId].inventoryに取り込む(yearとmonthが同じものは合算する)
            let inventoryAmount = 0;
            const yearMonthInfo: IYearMonth = {
              year: executionDate.getFullYear(),
              month: executionDate.getMonth() + 1,
            };
            for (let i = 0; i < inventoryData.length; i++) {
              const inventory = inventoryData[i];
              if (DateService.equalYearMonthInfo(inventory, yearMonthInfo)) {
                inventoryAmount += inventory.amount;
              }
            }
            inputFormArray[business.translateBusinessId].inventory = inventoryAmount;
            // MOQの読み取り
            if (data['MOQ']?.value == null || data['MOQ']?.value === '') {
              inputFormArray[business.translateBusinessId].MOQ = undefined; // MOQが空文字の場合は何も入れない
            } else {
              inputFormArray[business.translateBusinessId].MOQ = Number(data['MOQ']?.value); // MOQが空文字でない場合は数値に変換（バリデーションの対象に含める）
            }
          }

          // 基本パラメータに関して、validateを行う
          validateOutputFoundationParameters(inputFormArray[business.translateBusinessId], fileName, sheetName, data, inputErrorObj);

          // 同一CDだがNameに差分があるかのvalidateを行う
          validateOutputExcelDifferenceConfirmationCodeAndName(
            business,
            differenceConfirmationCodeAndName,
            fileName,
            sheetName,
            data,
            inputErrorObj
          );
        }
      }
      // 最後に、formArrayを更新
      // inputFormArray[business.translateBusinessId] = business;
      // キーが同じものはまとめる処理
      // 各々のインプット要素を受け取る
    });
    return output;
  };

  /**
   * TranslateExcelBusinessに過去の実績を追加する
   * すでに作成されているTranslateBusinessの「内示、販売実績」に、入力側の在庫表エクセルに登録されている数量情報を追加する
   * 数量は在庫表に登録されている全ての年月分(年、列名として登録されている分全て)を読む
   * @param targetBusinessElement IAnnounce[]またはISalesResult[]
   * @param excelData 在庫表から取り込んだエクセルの情報
   * @param dataType
   */
  public addPastAchievementInfo = <T extends AddPastAchievementInfoType>(
    targetBusinessElement: AddPastAchievementInfoDataByType<T>[],
    excelData: ExcelOutputRow,
    dataType: T
  ): void => {
    // 集計行取引の場合、targetBusinessElementが確実に空になるので、その場合の処理も飛ばす。
    // 集計行に入れる数量情報は、システム登録直前に行う集計操作で作成しているので、この時点ではいらない
    if (!targetBusinessElement || !targetBusinessElement.length) return;
    const regex = /^\d{6}_.+$/; // '年月_要素'の形のみに限定するための、正規表現 (年月は202310のように6桁の数字)
    // 在庫表エクセルをパースした情報を見て、必要なパラメータの取得を行う
    // forEachで回す前に、「年月_内示」、「年月_実績」(2023/10時点)の情報をfilterしてから行なっている
    const sortedArray = simpleCopy(targetBusinessElement).sort(DateService.compareYearMonths);
    Object.keys(excelData)
      .filter((key) => key.includes(dataType) && regex.test(key))
      .forEach((dataKey) => {
        // 年月の文字列(6桁のもの)から年と月の情報を分離する
        const yearMonthInfo = DateService.parseStringYearMonth(dataKey.split('_')[0]);
        // 登録済み内示情報を年月でソートする
        // 編集をかける内示または販売実績情報の配列(2023/10時点)に、追加したい年月の情報が存在するかを確認する
        const isExistingObj = targetBusinessElement?.some((obj) => DateService.equalYearMonthInfo(obj, yearMonthInfo));
        // 入力された内示エクセルから読み取った内示情報の一番若い年月情報を取得する
        const targetYearMonth: IYearMonth = {
          year: sortedArray[0].year,
          month: sortedArray[0].month,
        };
        // 入力内示情報の一番若い年月よりも前かどうか確認する
        // これより前の内示情報を過去情報として在庫表エクセルから読み取った情報で埋める
        const isBeforeAnnounce = DateService.compareYearMonths(targetYearMonth, yearMonthInfo);
        // 更新をかけたい年月の情報が、内示または販売実績情報の配列にない場合追加を行う
        if (!isExistingObj && isBeforeAnnounce > 0) {
          const amountValue = String(excelData[dataKey].value).parseNumber();
          // 内示または販売実績情報の配列に追加するオブジェクトの作成を行う
          // TODO: 型の安全性担保されてる? もっと良い書き方ありそう
          const updateObject: AddPastAchievementInfoDataByType<T> = {
            year: yearMonthInfo.year,
            month: yearMonthInfo.month,
            amount: amountValue,
            ...(dataType === '内示'
              ? { manageInitiatedYearMonth: DateService.getBeforeYearMonth({ year: yearMonthInfo.year, month: yearMonthInfo.month }) }
              : {}),
          } as AddPastAchievementInfoDataByType<T>;
          // 内示または販売実績情報の配列に追加する
          targetBusinessElement.push(updateObject);
        }
      });
  };

  /**
   * 転記処理を終えたworksheet情報から、取引数量の集計を行う
   * この処理は、転記済みエクセルに集計行が必要な場合(現状、仕入先内示エクセル)に行う
   * 「小計」、「合計」、「統計」といった集計項目ごとに数量の集計をエクセルのSUM関数のような感じで行う
   * @param workSheet 転記処理を終えたシート情報
   * @param option parseExcelObjectsで色々やるためのオプション
   * @param sheetRange シート内の有効範囲(情報が入力されている範囲)
   * @returns (worksheet?) 更新したワークシート情報を返す
   */
  private updateTotalAnnounceWorkSheet = (workSheet: any, option: ExcelParseOption, sheetRange?: string): any => {
    // sheetがundefinedなら抜ける
    if (!sheetRange) return workSheet;
    // 更新開始セル(年月データが入っているセル)のアドレスを取る
    const startAddress = XLSX.utils.decode_cell(option.separateYmd.startCell);
    const rangeVal = XLSX.utils.decode_range(sheetRange);

    // 小計、合計、統計といった集計行である事を示す値が入っている列を指定する
    // ↑は数量が入っている列の１つ左隣としている
    const aggregationColumn = startAddress.c - 1;

    // 各列の数量が入っている行を1行ずつ読み、集計行が合った場合はそこに集計行までの合計数量を入力する
    // 数量が入力されている列を1列ずつ見ていく
    for (let c = startAddress.c; c <= rangeVal.e.c; c = c + option.separateYmd.separateCount) {
      const rowNameAdr = XLSX.utils.encode_cell({ c: c, r: startAddress.r + 1 }); // 列名が入っているセル
      const rowNameValue = workSheet.cell(rowNameAdr).value(); // 列名
      // 空として登録されてるヘッダーについては無視する
      if (rowNameValue !== '' && rowNameValue) {
        // 集計用のオブジェクトの初期化
        const totalAmountObj: SupplierForecastAggregateInfo = {
          statistics: 0, // 統計用集計項目
          subTotal: 0, // 小計用集計項目
          totalAmount: 0, // 合計用集計項目
        };
        // 行ごとに見ていく
        for (let row = rangeVal.s.r + 2; row <= rangeVal.e.r; row += 1) {
          // 容量列の情報を取得する
          const aggregationAdr = XLSX.utils.encode_cell({ c: aggregationColumn, r: row }); // 容量列のアドレス
          const aggregationValue = workSheet.cell(aggregationAdr).value(); // 容量列の情報取得
          // 集計行の処理
          // 集計行がある場合、集計項目に合致するその行までの合計数量を入力する
          switch (aggregationValue) {
            case '小計':
              const subTotalAdr = XLSX.utils.encode_cell({ c: c, r: row });
              workSheet.cell(subTotalAdr).value(totalAmountObj.subTotal);
              totalAmountObj.subTotal = 0;
              break;
            case '合計':
              // 合計行では小計も初期化する
              const totalAmountAdr = XLSX.utils.encode_cell({ c: c, r: row });
              workSheet.cell(totalAmountAdr).value(totalAmountObj.totalAmount);
              totalAmountObj.subTotal = 0;
              totalAmountObj.totalAmount = 0;
              break;
            case '統計':
              const statisticsAdr = XLSX.utils.encode_cell({ c: c, r: row });
              workSheet.cell(statisticsAdr).value(totalAmountObj.statistics);
              totalAmountObj.statistics = 0;
              break;
            default:
              // 数量の集計処理
              // 集計行では無い場合に行う
              const announceAdr = XLSX.utils.encode_cell({ c: c, r: row }); // 数量が入っているアドレス
              // セルが空or0なら集計しない
              if (!workSheet.cell(announceAdr).value() || workSheet.cell(announceAdr).value() === '') {
                continue;
              }
              const cellValue = String(workSheet.cell(announceAdr).value()).parseNumber();
              totalAmountObj.statistics += cellValue;
              totalAmountObj.subTotal += cellValue;
              totalAmountObj.totalAmount += cellValue;
              break;
          }
        }
      }
    }
    return workSheet;
  };

  /**
   * ファイルタイプに合わせて必要であればrowKeyを更新した文字列を返す
   * @param fileType - ファイルタイプ
   * @param rowKey - rowKey
   * @param headerString - ヘッダーの文字列
   */
  public static updateRowKeyIfNeeded = (fileType: OutputFileType | ExportFileType, rowKey: string, headerString: string): string => {
    switch (fileType) {
      case '仕入先内示':
        return this.updateSupplierAnnounceRowKey(rowKey);
      case '仕入先内示出力':
        return ExportSupplierForecastExcelService.updateAmountColumnsRowKey(headerString, rowKey);
      default:
        return rowKey;
    }
  };

  /**
   * 仕入先内示エクセルを対象に、内示・販売実績数量が入っている列を表すkey(年_列名(月情報あり)の形)の年月更新処理を行う
   * 実装時の想定としては、仕入先内示エクセルへの転記時を対象にしている
   * @param rowKey businessId
   * @returns セルに入力する情報
   */
  public static updateSupplierAnnounceRowKey = (rowKey: string): string => {
    if (!rowKey) return rowKey; // 空セルを読む場合があるので回避
    // 列名に「月内示」、「月販売実績」が含まれている文字列を更新する
    if (rowKey.includes('月内示') || rowKey.includes('月販売実績')) {
      const yearStr = rowKey.split('_')[0];
      const monthStr = rowKey.split('_')[1];
      let updateYear = yearStr;
      // 正規表現を用いて、文字列内の数値データ(月)の更新処理を行う
      const updateMonth = monthStr.replace(/(\d+)/g, (match, p1) => {
        let nextMonth = Number(p1) + 1;
        if (nextMonth === 13) {
          // 月を+1する。
          // 12月の次は1月になるようにし、かつ年跨ぎにも対応
          updateYear = `${Number(yearStr) + 1}`;
          nextMonth = 1;
        }
        return `${nextMonth}`;
      });
      // 年_列名(月情報ある)形にして返す
      return updateYear + '_' + updateMonth;
    }
    // 更新対象以外は入力値をそのまま返す
    return rowKey;
  };

  /**
   *　入力されたエクセルの行情報をまとめたオブジェクトと出力対象のエクセルのセル情報を統合する
   * @param data　転記元の情報
   * @param output　出力対象のオブジェクト
   * @param indexKeyString　対象オブジェクトのキーの情報
   * @param excelRowInfo エクセルデータ(output)一行をkey-valueに変換した情報
   * @param fileType 出力するエクセルの種類
   * @param isDuplicatedBusiness 取引重複があるかどうかのフラグ、デフォルトはfalse
   */
  private translateOutputValues = (
    data: IArrival[] | IAnnounce[] | ISalesResult[] | string,
    output: ExcelOutputResult,
    indexKeyString: string,
    excelRowInfo: ExcelOutputRow,
    fileType: OutputFileType,
    isDuplicatedBusiness = false
  ) => {
    if (typeof data !== 'string') {
      data.forEach((value) => {
        let key = '';
        let rowName = '';
        let prevKey = ''; // 仕入先内示用、列移動前のセル情報
        // 在庫表と仕入先内示でkeyの作り方を分ける
        if (fileType === '在庫表') {
          // 202202みたいな形にする
          key = value.year + DateService.convertMonthWithZero(value.month) + '_' + indexKeyString; // 転記先の列名
          prevKey = key; // 転記前の列名(※在庫表は転記時に列の移動などが発生しないため、ここは転記先の列名と同じになる)
          // 変更履歴のために列名を持たせる
          rowName = value.month + '月' + indexKeyString;
        } else if (fileType === '仕入先内示') {
          // 2022_3月みたいな形にする
          key = value.year + '_' + value.month + '月' + indexKeyString; // 転記先の列名
          const nextMonthInfo = DateService.getShiftedIYearMonthByMonths(value.year, value.month, 1); // 年月の更新
          prevKey = nextMonthInfo.year + '_' + nextMonthInfo.month + '月' + indexKeyString; // 転記前の列名(※仕入先内示は、転記前後で列の位置が変わるため、転記前の列のアドレスを持たせる必要がある)
          // 変更履歴のために列名を持たせる
          rowName = value.month + '月' + indexKeyString;
        }
        const cellNumber = String(excelRowInfo[key]?.address) ?? ''; // セル番号は空文字を許さない為F
        let prevCellValue: string | number | undefined = '';
        if (excelRowInfo[prevKey]) prevCellValue = excelRowInfo[prevKey]?.value ?? '';

        if (fileType === '仕入先内示') {
          if (!excelRowInfo[prevKey]) prevCellValue = '';
          prevCellValue = excelRowInfo[prevKey]?.value ?? '';
        } else {
          prevCellValue = excelRowInfo[key]?.value ?? '';
        }
        const prevCellNumber = excelRowInfo[key]?.prevAddress ?? '';
        if (!output[cellNumber]) {
          output[cellNumber] = {
            value: undefined,
            prevValue: undefined,
            address: undefined,
            columnName: undefined,
            transactionInfo: undefined,
            isDuplicatedBusiness: false,
          };
        }
        if (indexKeyString === '(確)月末在庫') {
          // 一旦numberに戻して最後にstringに変換する
          output[cellNumber].value = checkFalsyNumber(Number(output[cellNumber].value ?? 0)) + (value?.amount ?? 0);
        } else {
          if (isDuplicatedBusiness) {
            // 取引重複がある場合、特定の警告文を入れる
            // TODO: 多言語化対応
            output[cellNumber].isDuplicatedBusiness = true;
            output[cellNumber].value = '取引重複があるため転記できません';
          } else {
            output[cellNumber].value = checkFalsyNumber(value?.amount);
          }
        }
        // 転記先の列名
        output[cellNumber].columnName = rowName;
        // 転記される取引の取引情報
        output[cellNumber].transactionInfo = [
          escapeStringComma(filterStringNumberUndefinedToString(excelRowInfo['置場名']?.value)),
          escapeStringComma(filterStringNumberUndefinedToString(excelRowInfo['商品CD']?.value)),
          escapeStringComma(filterStringNumberUndefinedToString(excelRowInfo['商品名']?.value)),
          escapeStringComma(filterStringNumberUndefinedToString(excelRowInfo['容量']?.value)),
          escapeStringComma(filterStringNumberUndefinedToString(excelRowInfo['仕入先名']?.value)),
          escapeStringComma(filterStringNumberUndefinedToString(excelRowInfo['売上先名']?.value)),
          escapeStringComma(filterStringNumberUndefinedToString(excelRowInfo['送り先名']?.value)),
        ];
        if (!output[cellNumber]?.prevValue) {
          output[cellNumber].prevValue = prevCellValue;
        }
      });
    } else {
    }
  };

  /**
   * 最終的に出力するWorkBook内の年月更新および最新月の内示数量の更新を行う
   * 実装時の想定としては、仕入先内示エクセルへの転記時を対象にしている
   * @param workSheet ワークシート情報
   * @param option parseExcelObjectsで色々やるためのオプション
   * @param sheetRange ワークシートの範囲
   * @returns (sheet型?) 更新を行なったsheet情報を返す
   */
  private updateSupplierAnnounceWorkSheet = (workSheet: any, option: ExcelParseOption, sheetRange?: string): any => {
    if (!sheetRange) return workSheet;
    // 更新開始セル(年月データが入っているセル)のアドレスを取る
    const startAddress = XLSX.utils.decode_cell(option.separateYmd.startCell);
    const rangeVal = XLSX.utils.decode_range(sheetRange);
    // 年月データが入っている列(内示・販売実績数量がある列)を対象に年月の更新を行なっていく
    for (let c = startAddress.c; c <= rangeVal.e.c; c = c + option.separateYmd.separateCount) {
      const yearAdr = XLSX.utils.encode_cell({ c: c, r: startAddress.r }); // 年データが入っているセル
      const YearCellValue = workSheet.cell(yearAdr).value();
      const rowNameAdr = XLSX.utils.encode_cell({ c: c, r: startAddress.r + 1 }); // 列名が入っているセル
      const rowNameValue = workSheet.cell(rowNameAdr).value();
      // 販売実績数量の列を基準として1ヶ月分の過去内示の転記、販売実績列の初期化を行う
      // startAddressの列に販売実績数量の列の右隣の列の内示数量を転記する
      // 空として登録されてるヘッダーについては無視する
      if (rowNameValue !== '' && rowNameValue && (rowNameValue.includes('販売実績') || rowNameValue.includes('内示'))) {
        if (rowNameValue.includes('月販売実績')) {
          // 販売実績数量列の初期化(入力されているすべての数量を削除する)
          for (let row = rangeVal.s.r + 1; row <= rangeVal.e.r; row += 1) {
            const oldAnnounceAdr = XLSX.utils.encode_cell({ c: c, r: row });
            // セルに数式が入っていた場合は更新を行わない
            if (workSheet.cell(oldAnnounceAdr).formula()) {
              continue;
            }
            workSheet.cell(oldAnnounceAdr).value('');
          }
          // 1ヶ月分の過去内示数量の更新処理
          for (let row = rangeVal.s.r + 1; row <= rangeVal.e.r; row += 1) {
            const oldAnnounceAdr = XLSX.utils.encode_cell({ c: startAddress.c, r: row });
            const latestAnnounceAdr = XLSX.utils.encode_cell({ c: c + 1, r: row });
            // セルに数式が入っていた場合は更新を行わない
            if (workSheet.cell(oldAnnounceAdr).formula()) {
              continue;
            }
            if (!workSheet.cell(latestAnnounceAdr).value() && workSheet.cell(latestAnnounceAdr).value() !== 0) {
              // 更新元のセルがundefinedなら、更新先は空で埋めるようにする
              workSheet.cell(oldAnnounceAdr).value('');
            } else {
              // 更新元のセルに数量が入っていれば、更新先はその数量で埋める
              workSheet.cell(oldAnnounceAdr).value(workSheet.cell(latestAnnounceAdr).value());
            }
          }
        } else if (c !== startAddress.c) {
          // 内示数量の更新処理
          // 更新された月の内示数量で各列埋め直す
          for (let row = rangeVal.s.r + 1; row <= rangeVal.e.r; row += 1) {
            const announceAdr = XLSX.utils.encode_cell({ c: c, r: row });
            const nextAnnounceAdr = XLSX.utils.encode_cell({ c: c + 1, r: row });
            const nextRowNameAdr = XLSX.utils.encode_cell({ c: c + 1, r: startAddress.r + 1 });
            // セルに数式が入っていた場合は更新を行わない(数式の保持)
            if (workSheet.cell(announceAdr).formula()) {
              continue;
            }
            if (!workSheet.cell(announceAdr).value() || !workSheet.cell(nextRowNameAdr).value()) {
              // 更新元のセルがundefinedまたは更新先の列名がundefinedなら、更新先は空で埋めるようにする
              workSheet.cell(announceAdr).value('');
            } else {
              // 更新元のセルに数量が入っていれば、更新先はその数量で埋める
              workSheet.cell(announceAdr).value(workSheet.cell(nextAnnounceAdr).value());
            }
          }
        }
        // 年_列名(月情報がある)の形の文字列にして、年月情報更新を行う
        const updatedRow = TranslateOutputFileManager.updateSupplierAnnounceRowKey(YearCellValue + '_' + rowNameValue);
        // WorkBook内の情報を更新する
        workSheet.cell(yearAdr).value(updatedRow.split('_')[0]);
        workSheet.cell(rowNameAdr).value(updatedRow.split('_')[1]);
      }
    }
    return workSheet;
  };
}
